import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { RouterLink } from "@angular/router";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import type { MenuItem } from "./compose-menu-items";
import { MainContainerStore } from "./main-container.store";

@Component({
  selector: "layout-main-container-sidebar-item",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent, RouterLink],
  template: `
    @if (item().routerLink) {
      <a
        class="hidden items-center gap-2 rounded-sm p-2 text-sm font-normal transition-colors duration-200 ease-in-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:flex"
        [routerLink]="item().routerLink"
      >
        @if (item().icon; as icon) {
          <fa-icon [icon]="icon" class="shrink-0" />
        }
        <span>{{ item().label }}</span>
      </a>
      <a
        class="flex items-center gap-2 rounded-sm p-2 text-sm font-normal transition-colors duration-200 ease-in-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:hidden"
        [routerLink]="item().routerLink"
        (click)="closeSidebar()"
      >
        @if (item().icon; as icon) {
          <fa-icon [icon]="icon" class="shrink-0" />
        }
        <span>{{ item().label }}</span>
      </a>
    } @else if (item().href) {
      <a
        class="hidden items-center gap-2 rounded-sm p-2 text-sm font-normal transition-colors duration-200 ease-in-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:flex"
        [attr.href]="item().href"
        [attr.target]="item().target ?? null"
        [attr.rel]="item().external ? 'noopener noreferrer' : null"
      >
        @if (item().icon; as icon) {
          <fa-icon [icon]="icon" class="shrink-0" />
        }
        <span>{{ item().label }}</span>
        @if (item().external) {
          <fa-icon [icon]="faArrowUpRightFromSquare" class="ml-auto shrink-0" />
        }
      </a>
      <a
        class="flex items-center gap-2 rounded-sm p-2 text-sm font-normal transition-colors duration-200 ease-in-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:hidden"
        [attr.href]="item().href"
        [attr.target]="item().target ?? null"
        [attr.rel]="item().external ? 'noopener noreferrer' : null"
        (click)="closeSidebar()"
      >
        @if (item().icon; as icon) {
          <fa-icon [icon]="icon" class="shrink-0" />
        }
        <span>{{ item().label }}</span>
        @if (item().external) {
          <fa-icon [icon]="faArrowUpRightFromSquare" class="ml-auto shrink-0" />
        }
      </a>
    } @else {
      <span class="flex items-center rounded-sm p-2 text-sm font-medium">
        @if (item().icon; as icon) {
          <fa-icon [icon]="icon" class="mr-2" />
        }
        {{ item().label }}
      </span>
    }
    <div class="ml-4">
      @for (child of item().children; track child.label) {
        <layout-main-container-sidebar-item [item]="child" />
      }
    </div>
  `,
  host: {
    role: "menuitem",
  },
})
export class MainContainerSidebarItem {
  // Dependencies
  readonly #mainContainerStore = inject(MainContainerStore);

  // Inputs
  readonly item = input.required<MenuItem>();
  readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;

  closeSidebar() {
    this.#mainContainerStore.toggleSidebarOpenState();
  }
}
